import type {
  CalendarSelections,
  CalendarServiceSelections,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import {
  getUrlQueryParamValue,
  BookingsQueryParams,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { getVariantByChoiceIdAndOptionId } from '../../../../utils/dynamicPricing/dynamicPricing';
import {
  DialogState,
  DialogType,
} from '../../ViewModel/dialogViewModel/dialogViewModel';
import { CalendarState } from '../../controller';
import { Basket } from '@wix/bookings-services-preferences-modal/types';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import { bookingsUouFlowYourAppointmentsModal } from '@wix/bi-logger-wixboost-ugc/v2';

export type OpenServicesPreferencesModal = () => Promise<void>;

export const createOpenServicesPreferencesModalAction =
  ({
    getControllerState,
    context,
  }: ActionFactoryParams<
    CalendarState,
    CalendarContext
  >): OpenServicesPreferencesModal =>
  async () => {
    const { createModalData } = await import(
      '@wix/bookings-services-preferences-modal/utils'
    );
    const { enrichModalData } = await import(
      '@wix/bookings-services-preferences-modal/controllerActions'
    );
    const [state, setState] = getControllerState();
    const { servicesInView, serviceVariantsMap } = state;
    const {
      languageSettings,
      calendarSelections,
      experiments,
      isMultiServiceAppointmentEnabled,
      businessInfo,
    } = context;

    if (!calendarSelections) {
      return;
    }

    const basket = getBasket({
      servicesInView,
      serviceVariantsMap,
      calendarSelections,
    });

    const resourceFromUrl = getUrlQueryParamValue(
      context.flowAPI.controllerConfig.wixCodeApi,
      BookingsQueryParams.RESOURCE,
    );

    const servicesPreferencesModalData = createModalData({
      regionalSettingsLocale: languageSettings?.regionalSettingsLocale!,
      service: servicesInView[0],
      basket,
      ...(isMultiServiceAppointmentEnabled ? {} : { maxServices: 1 }),
      experiments,
      bookingsSettings: businessInfo?.bookingsSettings,
      resourceFromUrl,
    });

    setState({
      servicesPreferencesModalData,
      dialog: { state: DialogState.IDLE, type: DialogType.ServicesPreferences },
    });

    enrichModalData({
      httpClient: context.flowAPI.httpClient,
      service: servicesInView[0],
      basket,
      getServicesPreferencesModalData: () =>
        getControllerState()[0].servicesPreferencesModalData!,
      setServicesPreferencesModalData: (data) =>
        setState({ servicesPreferencesModalData: data }),
      prefetchedData: {
        getServiceOptionsAndVariants: state.serviceVariantsMap,
      },
      experiments,
      constrains: servicesPreferencesModalData?.constrains,
    });
    context.biLogger.report(
      bookingsUouFlowYourAppointmentsModal({
        action: 'edit basket item from calendar',
        numberOfServices: basket.selectedServicesPreferences.length,
        isMultiFlow: isMultiServiceAppointmentEnabled,
      }),
    );
  };

const getBasket = ({
  servicesInView,
  serviceVariantsMap,
  calendarSelections,
}: {
  servicesInView: Service[];
  serviceVariantsMap: Record<string, any>;
  calendarSelections: CalendarSelections;
}): Basket => {
  const serviceIdServiceMap = servicesInView.reduce<Record<string, Service>>(
    (acc, service) => ({ ...acc, [service.id!]: service }),
    {},
  );
  const firstLocation = servicesInView?.[0].locations?.[0];
  const locationType = firstLocation?.type!;

  return {
    location: {
      type: locationType,
      value:
        calendarSelections.location ||
        firstLocation?.custom?.address?.formattedAddress ||
        '',
    },
    selectedServicesPreferences: calendarSelections.services.map(
      ({ id, resources, variants, addOns }: CalendarServiceSelections) => {
        const service = serviceIdServiceMap[id];
        let variant = variants?.[0];

        if (variant) {
          const serviceVariants = serviceVariantsMap[service.id!];
          const choiceId =
            variant.choices?.[0].custom ||
            variant.choices?.[0].staffMemberId! ||
            variant.choices?.[0].id!;
          const optionId = variant.choices?.[0].optionId!;
          const selectedVariant = getVariantByChoiceIdAndOptionId(
            serviceVariants,
            choiceId,
            optionId,
          );

          variant = selectedVariant;
        }

        return {
          service,
          resources: {
            staff: resources,
          },
          variant,
          addOns,
        };
      },
    ),
  };
};
